/**
 * How good a specific two-card holding is on a specific board, on a scale of about
 * 0 to 10.
 *
 * Not equity — equity costs a Monte Carlo run and this is called once per combination
 * in a range, thousands of times per decision. It is an ORDERING: good enough to say
 * which half of a range is the strong half, which is the only question the callers
 * ask of it.
 *
 * It lives in its own file because two things need it and they must not drift apart:
 * the policy uses it to work out what continues against a bet, and the read tracker
 * uses it to narrow a range by an action. If those two disagreed about which hands are
 * strong on a board, a bot would bet for value against the hands it had itself decided
 * were folding.
 */
import { classify } from "../handclass";
import type { Card } from "../types";

/**
 * A cheap ordering of hands on a board — good enough to say which half of a range is
 * the strong half, and thousands of times faster than running equity on each combo.
 * Uses the same classifier the bot uses on its own hand, so the two cannot disagree.
 */
export function boardStrength(a: Card, b: Card, board: readonly Card[]): number {
  const hc = classify([a, b], board);
  const base: Record<string, number> = {
    air: 0, pair: 3, two_pair: 6, trips: 7.5, straight: 8.5, flush: 9.2,
    full_house: 9.6, quads: 9.9, straight_flush: 10,
  };
  let v = base[hc.category] ?? 0;
  if (hc.category === "pair") {
    v += hc.pair === "over" ? 1.6 : hc.pair === "top" ? 1.3 : hc.pair === "second" ? 0.6
       : hc.pair === "third" ? 0.3 : 0;
    v += hc.kicker / 60;
  }
  if (hc.flushDraw) v += 1.5;
  if (hc.nutFlushDraw) v += 0.4;
  if (hc.openEnded) v += 1.2;
  else if (hc.gutshot) v += 0.5;
  if (hc.category === "air") v += hc.overcards * 0.25;
  if (hc.playingBoard) v = 1;
  return v;
}
